import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '@/lib/utils'

export const Tabs = TabsPrimitive.Root

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      /*
       * The row WRAPS rather than scrolls.
       *
       * An inline-flex sizes to its CONTENT, so a long tab row simply grew —
       * Admin's reaches 871px — and the page overflowed. Capping it with
       * `max-w-full` and scrolling fixed the overflow but left a worse
       * problem: at 1024px two of Admin's eight tabs sat past the edge with
       * nothing to say so, because macOS hides a scrollbar until you scroll.
       * "Display" was one of them, and it is where the green room screen is
       * configured.
       *
       * Wrapping shows every tab with no gesture to discover them. It is
       * self-adjusting and costs the short rows nothing: a 3-tab dialog row
       * never reaches the cap, so it never wraps, and `inline-flex` still
       * makes the pill hug its content.
       *
       * `min-h` rather than `h`, or a second row would be clipped to the
       * height of one. Shared by every tab row: song detail, plan detail,
       * admin, schedule.
       */
      'inline-flex min-h-[2.25rem] max-w-full flex-wrap items-center justify-start gap-1 rounded-lg bg-muted p-1 text-muted-foreground',
      className,
    )}
    {...props}
  />
))
TabsList.displayName = 'TabsList'

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className,
    )}
    {...props}
  />
))
TabsTrigger.displayName = 'TabsTrigger'

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-4 focus-visible:outline-none', className)}
    {...props}
  />
))
TabsContent.displayName = 'TabsContent'
